import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, styled, Toolbar, useMediaQuery, useTheme, Container, Alert, Button, Typography, IconButton } from '@mui/material';
import { WarningAmber as WarningIcon, Close as CloseIcon } from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import { useAppDispatch } from '@/store/redux';
import { logout, teamUserLogout } from '@/store/redux/slices/authSlice';
import { AppHeader, AppSidebar } from '@/components/layout';
import { basicMenuItems, merchantMenuItems, teamUserMenuItems } from '@/config/menuConfig';
import { clearAuthCookiesService } from '@/services/clearAuthCookiesService';
import { authService } from '@/services/authService';
import { profileService, type KycStatusResponse } from '@/services/profileService';
import { LoadingSpinner } from '@/components/ui';
import { getKycStatusMessage } from '@/utils/kycMessages';


const drawerWidth = 240;
const collapsedWidth = 64;

// Styled components for the new password change design
const ChangePasswordContainer = styled(Box)(({ theme }) => ({
  //display: 'flex',
  //flexDirection: 'column',
  //width: '100%',
  //height: '100%',
  backgroundColor: '#FCFCFF',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
}));

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [kycStatus, setKycStatus] = useState<KycStatusResponse | null>(null);
  const [kycLoading, setKycLoading] = useState(true);
  const [onboardingDismissed, setOnboardingDismissed] = useState(() =>
    sessionStorage.getItem('onboarding_remind_later') === 'true'
  );

  // Check KYC status (no redirect - show onboarding banner in header instead)
  useEffect(() => {
    const checkKyc = async () => {
      setKycLoading(true);
      try {
        const res = await profileService.getKycStatus();
        const data = res?.data as KycStatusResponse | undefined;
        setKycStatus(data ?? null);
      } catch {
        setKycStatus(null);
      } finally {
        setKycLoading(false);
      }
    };
    checkKyc();
  }, [location.pathname]);

  // Check if we're on mobile
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Handle mobile drawer toggle
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleAuthLogout = async () => {
    
    try {
      // Call server logout first (this clears server-side session)
      await authService.authLogout();
    } catch (error) {
      console.error('Server logout failed:', error);
    }
    
    // Dispatch Redux logout to clear state
    dispatch(logout());
    
    // Clear all auth data (cookies, localStorage, sessionStorage) and redirect
    clearAuthCookiesService.completeLogout();
  };

  const handleTeamUserLogout = async () => {
    
    try {
      // Call server logout first (this clears server-side session)
      await authService.authLogout();
    } catch (error) {
      console.error('Server logout failed:', error);
    }
    
    // Dispatch Redux team logout to clear state
    dispatch(teamUserLogout());
    
    // Clear all auth data (cookies, localStorage, sessionStorage) and redirect
    clearAuthCookiesService.completeLogout();
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleToggleClick = () => {
    if (isMobile) {
      handleDrawerToggle();
    } else {
      toggleSidebar();
    }
  };

  return (
    <>
    
      <style>
        {`
          .no-margin-left {
            margin-left: 0px !important;
            margin-top: 50px;
          }
        `}
      </style>
{/* Alert Title at Top - Fixed Position - Covers Sidebar */}
      {/* <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          width: '100vw',
          zIndex: theme => theme.zIndex.drawer + 10, // Higher than drawer
          margin: 0,
          padding: 0,
        }}
      >
        <Alert 
          severity="info" 
          icon={
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: '#17A6DA',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                i
              </Typography>
            </Box>
          }
          sx={{ 
            mb: 0,
            borderRadius: 0,
            backgroundColor: '#e8eefb',
            border: 'none',
            color: 'rgb(40, 42, 48)',
            py: 1,
            px: { xs: 2, sm: 3, md: 4 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100vw',
            boxSizing: 'border-box',
            margin: 0,
            '& .MuiAlert-icon': {
              mr: 0,
              mt: 0,
              flexShrink: 0,
              padding: 0,
            },
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              padding: 0,
              flexGrow: 1,
            }
          }}
        >
          <span>You are in testing environment.</span>
          <Button 
            size="small" 
            sx={{ 
              color: '#17A6DA', 
              textDecoration: 'underline',
              textTransform: 'none',
              p: 0,
              minWidth: 'auto',
              fontSize: '0.875rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              }
            }}
          >
            Learn more
          </Button>
        </Alert>
      </Box> */}
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        {/* Sidebar - Responsive */}
        <AppSidebar
          width={drawerWidth}
          collapsedWidth={collapsedWidth}
          collapsed={isMobile ? false : sidebarCollapsed}
          logo={{
            src: '/logos/logo-white_1.png',
            alt: 'GrowthBnk Logo',
            height: 40,
          }}
          menuItems={merchantMenuItems}
          currentPath={location.pathname}
          onNavigate={path => {
            navigate(path);
            if (isMobile) {
              setMobileOpen(false); // Close mobile drawer after navigation
            }
          }}
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={isMobile ? handleDrawerToggle : undefined}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <AppHeader
            title="GrowthBnk Merchant Dashboard"
            onboardingBanner={undefined}
            user={
              user
                ? {
                  name: user.name,
                  email: user.email,
                }
                : undefined
            }
            onAuthLogout={handleAuthLogout}
            onTeamUserLogout={handleTeamUserLogout}
            drawerWidth={drawerWidth}
            collapsed={sidebarCollapsed}
            onToggleSidebar={handleToggleClick}
          />
          <Container
            maxWidth={false}
            sx={{
              mt: { xs: 2, sm: 4 },
              mb: { xs: 2, sm: 4 },
              px: { xs: 2, sm: 3 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box
              component="main"
              className="no-margin-left"
              sx={{
                flexGrow: 1,
                bgcolor: 'background.default',
                minHeight: 'auto',
                transition: theme =>
                  theme.transitions.create('margin', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                  }),
                ml: { xs: 0, sm: `${sidebarCollapsed ? collapsedWidth : drawerWidth}px` },
                width: '100%',
              }}
            >
              {kycStatus && !kycStatus.kyc_status && !onboardingDismissed && !(kycStatus.kyc_code === 2 && kycStatus.basic_status === 2 && kycStatus.business_status === 2 && kycStatus.bank_status === 2) && location.pathname !== '/profile' ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '600px',
                      width: '100%',
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.primary.light}25 100%)`,
                      borderRadius: 4,
                      border: (theme) => `2px solid ${theme.palette.primary.light}`,
                      boxShadow: (theme) => `0 8px 24px ${theme.palette.primary.main}20`,
                      p: 4,
                      textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px',
                        boxShadow: (theme) => `0 4px 16px ${theme.palette.primary.main}40`,
                      }}
                    >
                      <WarningIcon sx={{ color: 'white', fontSize: 48 }} />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 700,
                        mb: 2,
                      }}
                    >
                      Account Activation Required
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        mb: 4,
                        lineHeight: 1.6,
                      }}
                    >
                      {getKycStatusMessage(kycStatus.basic_status, kycStatus.business_status, kycStatus.bank_status)}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/profile')}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 700,
                          px: 4,
                          py: 1.5,
                        }}
                      >
                        Activate Account Now
                      </Button>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => {
                          sessionStorage.setItem('onboarding_remind_later', 'true');
                          setOnboardingDismissed(true);
                        }}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 4,
                          py: 1.5,
                        }}
                      >
                        Remind Me Later
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: {
                      xs: '320px',
                      sm: '420px',
                      md: '700px',
                      lg: '100%',
                    },
                    maxWidth: '100%',
                    backgroundColor: '#FCFCFF',
                    borderRadius: theme => theme.spacing(2),
                    padding: theme => theme.spacing(3),
                    mx: 'auto',
                  }}
                >
                  {kycLoading && location.pathname !== '/profile' ? (
                    <LoadingSpinner title="Checking profile..." subtitle="Please wait" size="small" />
                  ) : (
                    <Outlet />
                  )}
                </Box>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};


export default MainLayout;


